import { useEffect } from 'react';

/**
 * Intercepts clicks on any anchor whose href points to the legacy JSP/Servlet
 * app (identified by the "/control" path segment used by getBackofficeServletBasename()).
 *
 * React Router's <Link> hijacks every click to perform client-side navigation,
 * even when the target href is an absolute URL pointing outside the SPA router
 * (e.g. the legacy backoffice servlet). This breaks navigation to legacy pages.
 *
 * This hook listens on the document in the capture phase (before React Router's
 * own click handler runs) and forces a full browser navigation for any link
 * targeting the legacy app, effectively exiting the React SPA.
 */
export function useLegacyAppLinkInterceptor() {
    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
            ) {
                return;
            }

            const anchor = (event.target as HTMLElement)?.closest?.('a');
            if (!anchor) {
                return;
            }

            if (anchor.target && anchor.target !== '_self') {
                return;
            }

            const href = anchor.getAttribute('href');
            if (!href || !href.includes('/control')) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();
            window.location.href = anchor.href;
        };

        document.addEventListener('click', handleClick, true);
        return () => document.removeEventListener('click', handleClick, true);
    }, []);
}
